import React, { useEffect, useMemo, useState } from "react";
import { GitCompare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function Compare() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState([]);

  useEffect(() => { api.get("/admin/submissions").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const runCompare = async () => {
    if (selected.length < 2) { toast.error("Select at least 2 submissions to compare."); return; }
    const res = await api.post("/admin/submissions/compare", { assessment_ids: selected });
    setResult(res.data);
  };

  const componentNames = useMemo(() => {
    const set = [];
    result.forEach((r) => r.components.forEach((c) => { if (!set.find((x) => x.name === c.name)) set.push({ name: c.name, is_legacy: c.is_legacy }); }));
    return set;
  }, [result]);

  return (
    <div data-testid="compare-page">
      <div className="overline mb-2">Benchmarking</div>
      <h1 className="font-serif text-4xl text-oxford tracking-tight">Compare Submissions</h1>

      <div className="bg-white border border-[var(--brdr)] rounded-sm shadow-sm mt-8">
        <div className="p-5 border-b border-[var(--brdr)] flex items-center justify-between">
          <h3 className="font-serif text-lg text-oxford">Select submissions ({selected.length})</h3>
          <Button data-testid="run-compare-btn" onClick={runCompare} className="btn-primary text-white h-10 px-6">
            <GitCompare className="mr-2 h-4 w-4" /> Compare
          </Button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-[var(--brdr)]">
          {rows.map((r) => (
            <label key={r.assessment_id} data-testid={`compare-select-${r.assessment_id}`}
              className="flex items-center gap-4 px-5 py-3 hover:bg-alabaster cursor-pointer">
              <Checkbox checked={selected.includes(r.assessment_id)} onCheckedChange={() => toggle(r.assessment_id)} />
              <div className="flex-1">
                <div className="text-sm font-medium text-oxford">{r.name} · {r.company}</div>
                <div className="text-xs text-[var(--text2)]">{r.sector}</div>
              </div>
              <div className="font-mono text-sm text-oxford">B {r.business_score?.toFixed(1)}</div>
              <div className="font-mono text-sm text-gold">L {r.legacy_score?.toFixed(1) ?? "—"}</div>
            </label>
          ))}
          {!rows.length && <div className="px-5 py-10 text-center text-sm text-[var(--text2)]">No completed submissions yet.</div>}
        </div>
      </div>

      {result.length > 0 && (
        <div className="bg-white border border-[var(--brdr)] rounded-sm shadow-sm mt-6 overflow-x-auto" data-testid="compare-result">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--brdr)]">
                <th className="px-5 py-4 text-left overline">Dimension</th>
                {result.map((r) => <th key={r.assessment_id} className="px-5 py-4 text-left overline whitespace-nowrap">{r.company}<div className="text-[10px] normal-case tracking-normal text-[var(--text2)] font-normal">{r.name}</div></th>)}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--brdr)] bg-alabaster">
                <td className="px-5 py-4 font-medium text-oxford">Business Score</td>
                {result.map((r) => <td key={r.assessment_id} className="px-5 py-4 font-mono text-oxford">{r.business_score?.toFixed(1)}</td>)}
              </tr>
              <tr className="border-b border-[var(--brdr)] bg-alabaster">
                <td className="px-5 py-4 font-medium text-oxford">Legacy</td>
                {result.map((r) => <td key={r.assessment_id} className="px-5 py-4 font-mono text-gold">{r.legacy_score?.toFixed(1) ?? "—"}</td>)}
              </tr>
              {componentNames.map((cn) => (
                <tr key={cn.name} className="border-b border-[var(--brdr)] last:border-0">
                  <td className="px-5 py-4 text-[var(--text2)]">{cn.name}</td>
                  {result.map((r) => {
                    const c = r.components.find((x) => x.name === cn.name);
                    return <td key={r.assessment_id} className="px-5 py-4 font-mono text-oxford">{c ? c.score.toFixed(1) : "—"}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
